import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { bandwidthAPI } from '../../services/api';
import { useAuthStore } from '../../store';
import { pageHidden } from '../../components/Sidebar/menuConfig';
import { speedText, percentOf, CHANGE_LABELS } from '../../utils/bandwidthFormat';
import '../BandwidthManager/BandwidthManager.css';
import './Dashboard.css';

const REFRESH_MS = 30 * 1000;

/**
 * Bandwidth now, on the dashboards: how fast the guests online are using the internet,
 * each gateway's link and how full it is, the latest live speed changes — with the way
 * to Internet Plans → Live Bandwidth. Nothing where there is no gateway (or no Sessions → View).
 */
export default function BandwidthCard({ branchId, tenantId }) {
    const access = useAuthStore((state) => state.access);
    const [data, setData] = useState(null);

    const load = useCallback(async () => {
        try {
            const params = {};
            if (branchId) params.branchId = branchId;
            if (tenantId) params.tenantId = tenantId;
            const r = await bandwidthAPI.overview(params);
            setData(r.data.data);
        } catch (_) { setData(null); }
    }, [branchId, tenantId]);

    useEffect(() => {
        load();
        const t = setInterval(load, REFRESH_MS);
        return () => clearInterval(t);
    }, [load]);

    if (!data?.gateways?.length) return null;
    const { totals } = data;
    const latest = (data.changes || []).slice(0, 5);

    return (
        <div className="ad-card" style={{ marginTop: '1.5rem' }}>
            <div className="ad-card-header">
                <div className="ad-card-title">
                    <i className="fas fa-sliders-h"></i>
                    <h2>Bandwidth now</h2>
                    <span className="ad-live-badge"><span className="ad-pulse-dot"></span>{totals.sessions} online</span>
                </div>
                {!pageHidden('/plans', access) && <Link className="ad-refresh-small" to="/plans?tab=bandwidth">Internet Plans → Live Bandwidth <i className="fas fa-arrow-right"></i></Link>}
            </div>
            <div style={{ padding: '1.125rem 1.375rem' }}>
                <div className="bw-totals" role="group" aria-label="Bandwidth figures">
                    {[
                        ['Download now', speedText(totals.use?.downloadKbps)],
                        ['Upload now', speedText(totals.use?.uploadKbps)],
                        ['On a dynamic plan', totals.dynamicSessions],
                        ['Speed set by an admin', totals.pinnedSessions],
                        ['Speed changes (24 h)', totals.changes24h],
                    ].map(([label, value]) => (
                        <div key={label} className="bw-total">
                            <span className="bw-total-label">{label}</span>
                            <span className="bw-total-value">{value ?? 0}</span>
                        </div>
                    ))}
                </div>
                <div className="bw-gateways">
                    {data.gateways.map((g) => {
                        const pct = percentOf(g.use.downloadKbps, g.dynamicBandwidth?.downloadCapacity);
                        return (
                            <div key={g._id} className="bw-gateway">
                                <div className="bw-gateway-head">
                                    <strong>{g.name}</strong>
                                    <span className={`bw-pill${g.dynamicBandwidth?.enabled ? ' on' : ''}`}>{g.dynamicBandwidth?.enabled ? 'Dynamic on' : 'Dynamic off'}</span>
                                </div>
                                <div className="bw-meter-row">
                                    <span className="bw-meter-label">Download</span>
                                    <span className="bw-meter-text">
                                        {speedText(g.use.downloadKbps) === '—' ? '0' : speedText(g.use.downloadKbps)}
                                        {g.dynamicBandwidth?.downloadCapacity ? ` of ${speedText(g.dynamicBandwidth.downloadCapacity)}` : ''}
                                        {' · '}{g.sessions} online
                                    </span>
                                    {pct !== null && (
                                        <span className="bw-meter" role="meter" aria-label={`${g.name} download in use`} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                                            <span className={`bw-meter-fill${pct >= 90 ? ' full' : ''}`} style={{ width: `${pct}%` }}></span>
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {latest.length > 0 && (
                    <>
                        <h3 className="bw-section-title" style={{ marginTop: 16 }}>Latest speed changes</h3>
                        <ul className="bwc-changes">
                            {latest.map((c) => (
                                <li key={c._id}>
                                    <span className="bw-note">{new Date(c.createdAt).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}</span>
                                    <span>{c.username || c.mac}</span>
                                    <span className="bw-nowrap">↓ {speedText(c.from?.downloadKbps)} → <strong>{speedText(c.to?.downloadKbps)}</strong></span>
                                    <span className="bw-note">{c.source === 'admin' ? c.actorName || 'Admin' : CHANGE_LABELS[c.source]}</span>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
        </div>
    );
}
